import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Chart as MixedChart, getElementAtEvent } from 'react-chartjs-2';
import {
	BarController,
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LinearScale,
	LineElement,
	LogarithmicScale,
	PointElement,
	TimeScale,
	Title,
	Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import cn from 'classnames';

import { prepareData, prepareOptions } from '@utils/kpi-utils/chart';

import { IChartProps } from './Chart.types';

import { timezoneUTC } from '@constants/common';

import './Chart.scss';

const AXES_HEIGHT = 30;

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	PointElement,
	LineElement,
	annotationPlugin,
	LogarithmicScale,
	BarController,
	TimeScale,
);

const Chart = ({
	charts,
	onClick,
	isLegend = true,
	isThreshold = false,
	isLogarithmic = false,
	height,
	isAnimation = true,
	isMaintainAspectRatio,
	colorIndex,
	fitWindowHeight = false,
	showAxesTitles = false,
	isAxisVisible = false,
	legendPlacement = 'bottom',
}: IChartProps) => {
	const chartRef = useRef<any>(null);
	const [visibleAxes, setVisibleAxes] = useState(() => {
		return Array.from(new Set(charts.map((_, index) => index).flat()));
	});
	const timeAggregationType = charts[0]?.yaxisAggregationType.name;

	const [options, setOptions] = useState(() => charts.length
		? prepareOptions({
			charts,
			timeAggregationType,
			isLegend,
			isThreshold,
			isLogarithmic,
			height,
			isAnimation,
			isMaintainAspectRatio,
			organizationTimezone: timezoneUTC,
			showAxesTitles,
			isAxisVisible,
			setVisibleAxes,
			visibleAxes,
			legendPlacement,
		})
		: {});

	useEffect(() => {
		if (!charts.length) return;

		setOptions(prepareOptions({
			charts,
			timeAggregationType,
			isLegend,
			isThreshold,
			isLogarithmic,
			height,
			isAnimation,
			isMaintainAspectRatio,
			organizationTimezone: timezoneUTC,
			showAxesTitles,
			isAxisVisible,
			setVisibleAxes,
			visibleAxes,
			legendPlacement,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isThreshold, isLogarithmic, charts, height, showAxesTitles, isAxisVisible, visibleAxes]);

	const [data, setData] = useState(() => charts.length ? prepareData(charts, colorIndex, isAxisVisible) : {});

	useEffect(() => {
		setData(charts.length ? prepareData(charts, colorIndex, isAxisVisible, isLogarithmic) : {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [charts, timeAggregationType, isLogarithmic]);

	const onClickChart = (event: React.MouseEvent<HTMLCanvasElement>) => {
		if (!onClick) return;

		const { current: chart } = chartRef;

		if (!chart) return;

		const elements = getElementAtEvent(chart, event);

		if (!elements || elements.length === 0) return;

		const element = elements[0];

		if (typeof element?.datasetIndex !== 'undefined' && typeof element?.index !== 'undefined') {
			if (charts.length > 1) {
				const { index } = element;
				const listOfDates = charts.map(chart => chart.lines[0].points[index].x);
				const isAvailableDrillDown = charts.every(chart => chart.lines[0].points[index].isAvailableDrillDown);

				onClick(listOfDates, isAvailableDrillDown);
			} else {
				const { datasetIndex, index } = element;
				const selectedDate = charts[0].lines[datasetIndex].points
					.find((_item, currentIndex) => currentIndex === index);
				const isAvailableDrillDown = selectedDate?.isAvailableDrillDown;

				if (selectedDate) {
					onClick([selectedDate.x], isAvailableDrillDown);
				}
			}
		}
	};

	const calcAxesHeight = useMemo(() => visibleAxes.length * AXES_HEIGHT, [visibleAxes.length]);
	const containerStyle = fitWindowHeight && isAxisVisible
		? { height: `calc(100vh - 350px + ${calcAxesHeight}px)` } : {};

	return (
		<div
			className={cn('f4gChartContainer', {
				'f4gChartContainerFitWindowHeight': fitWindowHeight,
			})}
			style={containerStyle}>
			{!!charts.length
				&& <>
					<MixedChart
						ref={chartRef}
						data={JSON.parse(JSON.stringify(data)) as any}
						height={height}
						onClick={onClickChart}
						options={options}
						type='bar'
						updateMode='default'
					/>
				</>
			}
		</div>
	);
};

export default memo(Chart);
